import React, { useEffect, useState } from 'react';
import { Sparkles, Plus, Loader2, Edit2, Trash2, X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Condition, ConditionInput, CONDITION_OPTIONS, conditionsAPI } from '@/services/library-mgmt-api';

const ConditionsModule: React.FC = () => {
  const [items, setItems] = useState<Condition[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Condition | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await conditionsAPI.list();
      setItems(data.results);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const handleDelete = async (c: Condition) => {
    if (!confirm(`Delete condition "${c.name}"?`)) return;
    try {
      await conditionsAPI.remove(c.id);
      toast.success('Condition deleted');
      void refresh();
    } catch { /* interceptor */ }
  };

  return (
    <div className="space-y-5 max-w-[1400px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-amber-500" />
            Conditions
          </h1>
          <p className="text-sm text-gray-500 mt-1">Book-copy condition grades and lending rules.</p>
        </div>
        <button
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="border border-amber-400 bg-amber-500 text-white hover:brightness-110 font-medium text-sm rounded-lg px-4 py-2 transition-colors flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          New condition
        </button>
      </div>

      {loading ? (
        <div className="bg-white border border-[#E8E4DC] rounded-xl py-16 flex items-center justify-center text-gray-400 text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E8E4DC] rounded-xl py-16 text-center text-sm text-gray-400">
          No conditions defined. Create one to start grading copies.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => {
            const score = parseFloat(c.score);
            return (
              <div key={c.id} className="bg-white border border-[#E8E4DC] rounded-xl p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-base font-semibold text-gray-900">{c.name_display ?? c.name}</div>
                    <div className="text-[11px] font-mono uppercase tracking-wider text-gray-400 mt-0.5">
                      {c.book_count} cop{c.book_count === 1 ? 'y' : 'ies'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">{score.toFixed(2)}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-gray-400">Score</div>
                  </div>
                </div>
                {c.description && <p className="mt-3 text-xs text-gray-500 line-clamp-2">{c.description}</p>}
                <div className="mt-4 flex flex-wrap gap-1.5">
                  <Pill
                    tone={c.can_be_borrowed ? 'ok' : 'red'}
                    icon={c.can_be_borrowed ? CheckCircle2 : AlertTriangle}
                  >
                    {c.can_be_borrowed ? 'Borrowable' : 'Not lendable'}
                  </Pill>
                  {c.requires_special_handling && (
                    <Pill tone="amber" icon={AlertTriangle}>Special handling</Pill>
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-[#F0EDE5] flex items-center justify-end gap-3">
                  <button
                    onClick={() => { setEditing(c); setShowForm(true); }}
                    className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                    Edit
                  </button>
                  <button
                    onClick={() => void handleDelete(c)}
                    className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-600 font-medium"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConditionForm
        open={showForm}
        editing={editing}
        existingNames={items.map((item) => item.name)}
        onClose={() => setShowForm(false)}
        onSaved={() => { setShowForm(false); void refresh(); }}
      />
    </div>
  );
};

const Pill: React.FC<{
  children: React.ReactNode;
  tone: 'ok' | 'amber' | 'red';
  icon?: React.ComponentType<{ className?: string }>;
}> = ({ children, tone, icon: Icon }) => {
  const styles =
    tone === 'ok'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
      : tone === 'amber'
        ? 'bg-amber-50 text-amber-700 border-amber-100'
        : 'bg-red-50 text-red-600 border-red-100';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border ${styles}`}>
      {Icon && <Icon className="h-3 w-3" />}
      {children}
    </span>
  );
};

const ConditionForm: React.FC<{
  open: boolean;
  editing: Condition | null;
  existingNames: string[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, editing, existingNames, onClose, onSaved }) => {
  const [form, setForm] = useState<ConditionInput>({
    name: '',
    description: '',
    score: 3,
    can_be_borrowed: true,
    requires_special_handling: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name,
        description: editing.description ?? '',
        score: parseFloat(editing.score),
        can_be_borrowed: editing.can_be_borrowed,
        requires_special_handling: editing.requires_special_handling,
      });
    } else {
      setForm({ name: '', description: '', score: 3, can_be_borrowed: true, requires_special_handling: false });
    }
  }, [editing, open]);

  const submit = async () => {
    if (!form.name) {
      toast.error('Condition category is required');
      return;
    }
    if (!form.description.trim()) {
      toast.error('Description is required');
      return;
    }
    if (!editing && existingNames.includes(form.name)) {
      toast.error('That condition category already exists');
      return;
    }
    if (form.score < 0 || form.score > 5) {
      toast.error('Score must be between 0 and 5');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, description: form.description.trim() };
      if (editing) {
        await conditionsAPI.update(editing.id, payload);
        toast.success('Condition updated');
      } else {
        await conditionsAPI.create(payload);
        toast.success('Condition created');
      }
      onSaved();
    } catch { /* interceptor */ } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit condition' : 'New condition'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <FormField label="Name">
            <select
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={Boolean(editing)}
              className="w-full h-9 px-3 rounded-lg bg-white border border-[#E8E4DC] text-sm focus:outline-none focus:border-amber-400"
            >
              <option value="">Select a condition category</option>
              {CONDITION_OPTIONS.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={!editing && existingNames.includes(option.value)}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Score (0–5)">
            <input
              type="number"
              min={0}
              max={5}
              step={0.25}
              value={form.score}
              onChange={(e) => setForm({ ...form, score: Number(e.target.value) })}
              className="w-full h-9 px-3 rounded-lg bg-white border border-[#E8E4DC] text-sm focus:outline-none focus:border-amber-400"
            />
          </FormField>
          <FormField label="Description">
            <textarea
              value={form.description ?? ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 rounded-lg bg-white border border-[#E8E4DC] text-sm focus:outline-none focus:border-amber-400"
            />
          </FormField>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.can_be_borrowed}
              onChange={(e) => setForm({ ...form, can_be_borrowed: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-300"
            />
            Allow borrowing in this condition
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.requires_special_handling}
              onChange={(e) => setForm({ ...form, requires_special_handling: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-300"
            />
            Requires special handling
          </label>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1">
            <X className="h-4 w-4" /> Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium bg-amber-500 text-white rounded-lg hover:brightness-110 transition-colors disabled:opacity-60 flex items-center gap-1"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {editing ? 'Save changes' : 'Create'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FormField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="text-xs font-mono uppercase tracking-wider text-gray-500 block mb-1.5">{label}</label>
    {children}
  </div>
);

export default ConditionsModule;
